import React from 'react'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa6'
import { GoArrowRight } from 'react-icons/go'
import { MdStorefront, MdHome, MdInventory2, MdAdd, MdCheck } from 'react-icons/md'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import useWhereLocation from './useWhereLocation'
import { WR } from '../../components/ui/tokens'

// VISUAL RELOOKING ONLY — same useWhereLocation selection logic, same
// dropdowns/handlers and navigation as before.
const Where = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedAddress = useSelector(state => state.cart.userInfo.selectedAddress);
  const selectedPoint = useSelector(state => state.cart.userInfo.selectedPoint);
  const selectedShop = useSelector(state => state.cart.userInfo.selectedShop);
  const woshUserId = useSelector(state => state.cart.userInfo.woshUserId);
  const {
    deliveryAddressData,
    collectionPointData,
    shopData,
    isOpenShop,
    isOpenAddress,
    isOpenPoint,
    toggleDropdownShop,
    toggleDropdownAddress,
    toggleDropdownPoint,
    onSelectShop,
    onSelectAddress,
    onSelectPoint,
    isNextDisabled
  } = useWhereLocation('pickup')

  const accent = WR.color.brand

  const handleNavigatePickup = () => navigate('/whenShop')
  const handleNavigateDeliver = () => navigate('/whereToDeliver')
  const handleNavigateDeliveryDetail = () =>
    navigate('/deliverydetails', { state: { source: 'wherepickup', details: 'Pickup Details' } })
  const handleNavigateCollectipPoint = () =>
    navigate('/collectionpoints', { state: { source: 'wherepickup', details: 'Pickup Details' } })

  // One location card (Shop / Home / Collection point). Selection still happens
  // through the dropdown item onClick; the Select/Selected button is status-only
  // (as before).
  const LocationCard = ({ label, active, icon, onAdd, isOpen, toggle, options, optionLabel, onSelect, current, fallback }) => (
    <div
      style={{
        background: '#fff',
        borderRadius: 24,
        border: `1.5px solid ${active ? accent : WR.color.border}`,
        boxShadow: WR.shadow.card,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'relative',
          padding: '14px 18px',
          background: active ? accent : WR.color.lavSel,
          color: active ? '#fff' : WR.color.encre,
          fontSize: 16,
          fontWeight: 800,
          textAlign: 'center',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
        }}
      >
        {label}
        {onAdd && (
          <button
            onClick={onAdd}
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 30,
              padding: '0 12px',
              borderRadius: 999,
              background: active ? 'rgba(255,255,255,0.2)' : '#fff',
              color: active ? '#fff' : accent,
              fontSize: 12,
              fontWeight: 700,
              border: `1px solid ${active ? 'transparent' : WR.color.border}`,
              cursor: 'pointer',
            }}
          >
            {t("ADD")}
            <MdAdd size={15} />
          </button>
        )}
      </div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, flex: 1 }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 24,
            background: WR.color.lavSel,
            color: WR.color.brand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>

        {/* dropdown */}
        <div style={{ position: 'relative', width: '100%' }}>
          <button
            onClick={toggle}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              height: 48,
              padding: '0 16px',
              borderRadius: 14,
              border: `1px solid ${WR.color.border}`,
              justifyContent: 'space-between',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: WR.color.encre, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {current || fallback}
            </span>
            {isOpen ? <FaAngleUp size={16} color={WR.color.inkSoft} /> : <FaAngleDown size={16} color={WR.color.inkSoft} />}
          </button>
          {isOpen && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 'calc(100% + 6px)',
                background: '#fff',
                borderRadius: 14,
                boxShadow: WR.shadow.float,
                border: `1px solid ${WR.color.border}`,
                zIndex: 20,
                maxHeight: 220,
                overflowY: 'auto',
              }}
            >
              <ul style={{ listStyle: 'none', margin: 0, padding: 6 }}>
                {options?.map((opt, index) => (
                  <li
                    key={index}
                    onClick={() => onSelect(opt)}
                    style={{ padding: '10px 12px', borderRadius: 10, fontSize: 14, color: WR.color.encre, cursor: 'pointer' }}
                  >
                    {optionLabel(opt)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* select / selected (status only) */}
        {active ? (
          <div style={{ width: '100%', height: 48, borderRadius: 14, background: accent, color: '#fff', fontSize: 15, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
            <MdCheck size={18} /> {t("SELECTED")}
          </div>
        ) : (
          <div style={{ width: '100%', height: 48, borderRadius: 14, background: WR.color.lavSoft, color: WR.color.brand, fontSize: 15, fontWeight: 700, border: `1px solid ${WR.color.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {t("SELECT")}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Header />
      <div style={{ background: WR.color.bg, minHeight: 'calc(100vh - 67px)', fontFamily: WR.font.sans, padding: 'clamp(24px,5vh,56px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          {woshUserId && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '6px 13px',
                borderRadius: 999,
                background: 'rgba(61, 69, 134, 0.08)',
                color: accent,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} />
              {woshUserId}
            </span>
          )}
          <h1 style={{ fontSize: 'clamp(24px,3vw,32px)', fontWeight: 800, letterSpacing: -0.5, margin: '8px 0 26px', color: WR.color.encre }}>
            {t("WHERE_TO_PICK_UP")}
          </h1>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 22 }}>
            <LocationCard
              label={t("SHOP")}
              active={!!selectedShop}
              icon={<MdStorefront size={46} />}
              isOpen={isOpenShop}
              toggle={toggleDropdownShop}
              options={shopData}
              optionLabel={(s) => s.name_shop}
              onSelect={onSelectShop}
              current={selectedShop}
              fallback={'Default Shop'}
            />
            <LocationCard
              label={t("DELIVERY_ADDRESS")}
              active={!!selectedAddress}
              icon={<MdHome size={46} />}
              onAdd={handleNavigateDeliveryDetail}
              isOpen={isOpenAddress}
              toggle={toggleDropdownAddress}
              options={deliveryAddressData}
              optionLabel={(a) => a.address}
              onSelect={onSelectAddress}
              current={selectedAddress}
              fallback={'Default address'}
            />
            <LocationCard
              label={t("COLLECTION_POINT")}
              active={!!selectedPoint}
              icon={<MdInventory2 size={46} />}
              onAdd={handleNavigateCollectipPoint}
              isOpen={isOpenPoint}
              toggle={toggleDropdownPoint}
              options={collectionPointData}
              optionLabel={(p) => p.collection_points_name}
              onSelect={onSelectPoint}
              current={selectedPoint}
              fallback={'Default CP'}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, marginTop: 30 }}>
            <button
              onClick={handleNavigateDeliver}
              style={{ height: 48, padding: '0 22px', borderRadius: 999, background: '#fff', border: `1px solid ${WR.color.border}`, fontSize: 14, fontWeight: 700, color: WR.color.encre, cursor: 'pointer' }}
            >
              {t("USE_DIFFERENT_LOCATION")}
            </button>
            <button
              onClick={handleNavigatePickup}
              disabled={isNextDisabled}
              style={{
                height: 56,
                padding: '0 56px',
                borderRadius: 999,
                background: isNextDisabled ? 'rgba(61, 69, 134, 0.2)' : accent,
                color: isNextDisabled ? 'rgba(0,0,0,0.5)' : '#fff',
                fontSize: 16,
                fontWeight: 700,
                border: 0,
                boxShadow: isNextDisabled ? 'none' : WR.shadow.cta,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: isNextDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {t("NEXT")}
              <GoArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default Where
